import { Box, type SxProps, type Theme } from "@mui/material";
import { type SyntheticEvent, useCallback, useMemo } from "react";
import {
  DOCUMENTATION_IFRAME_SANDBOX,
  buildDocumentationSrcDoc,
  classifyDocumentationHref,
} from "shared/documentation";

const styles = {
  frame: {
    width: "100%",
    height: "70vh",
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    bgcolor: "common.white",
  } satisfies SxProps<Theme>,
};

/**
 * Renders an uploaded document exactly as customers will see it, in the same
 * sandboxed frame. Links to other documents are inert here, because the
 * preview has no library to navigate; section links scroll, and external
 * links open in a new tab.
 */
const DocumentationPreview = ({ html }: { readonly html: string }) => {
  const srcDoc = useMemo(() => buildDocumentationSrcDoc(html), [html]);

  const handleLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
    const doc = event.currentTarget.contentDocument;
    const view = doc?.defaultView;
    if (
      doc === null ||
      doc === undefined ||
      view === null ||
      view === undefined
    ) {
      return;
    }
    doc.addEventListener("click", (click) => {
      // The frame is its own realm: test against its constructors, not ours.
      if (!(click.target instanceof view.Element)) {
        return;
      }
      const anchor = click.target.closest("a[href]");
      const link = classifyDocumentationHref(
        anchor?.getAttribute("href") ?? "",
      );
      if (link === null || link.kind === "external") {
        return;
      }
      click.preventDefault();
      if (link.kind === "fragment") {
        doc
          .querySelector(`#${view.CSS.escape(link.fragment)}`)
          ?.scrollIntoView();
      }
    });
  }, []);

  return (
    <Box
      component="iframe"
      title="Document preview"
      sandbox={DOCUMENTATION_IFRAME_SANDBOX}
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      sx={styles.frame}
      onLoad={handleLoad}
    />
  );
};

export default DocumentationPreview;
